/**
 * Vitrine Resurrection Mobile (remobiles.com) — refonte 2026-09 validée par Carlos :
 * photo de la boutique en fond, suivi de réparation, 3 espaces, CADBOT (chat + mini robot 3D),
 * services, diagnostic 35–80 €, horaires animés (heure de Cayenne), infos légales.
 * Aucune donnée interne : prix affichés = prix publics (chat serveur / tarifs), jamais la formule.
 */
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Smartphone, MapPin, Shield, MessageCircle, Phone, Bot, X, Zap, LifeBuoy, Sparkles,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import GoogleReviewsBlock from '../components/home/GoogleReviewsBlock';
import DailyPromoBanner from '../components/DailyPromoBanner';
import TrackRepair from '../components/landing/TrackRepair';
import CadbotChat from '../components/landing/CadbotChat';
import HoursLive from '../components/landing/HoursLive';
import '../components/landing/landing.css';
import CaricatureModal from '../components/caricature/CaricatureModal';

const MiniCadbot = React.lazy(() => import('../components/landing/MiniCadbot'));

const API = process.env.REACT_APP_BACKEND_URL || '';
const STORE_PHOTO = '/img/magasin-devant.jpg';
const STORE_PHOTO_SMALL = '/img/magasin-devant-960.jpg';
const DEFAULT_ADDRESS = '102 Avenue Général de Gaulle, Porte 2, 97300 Cayenne';
const DEFAULT_PHONE = '0694274051';

// Services : icône + clés i18n (le libellé/description sont traduits au rendu).
const SERVICES = [
  { icon: '📱', titleKey: 'landing.svcScreenTitle', descKey: 'landing.svcScreenDesc' },
  { icon: '🔋', titleKey: 'landing.svcBatteryTitle', descKey: 'landing.svcBatteryDesc' },
  { icon: '🔌', titleKey: 'landing.svcConnectorTitle', descKey: 'landing.svcConnectorDesc' },
  { icon: '📷', titleKey: 'landing.svcCameraTitle', descKey: 'landing.svcCameraDesc' },
  { icon: '💧', titleKey: 'landing.svcOxidationTitle', descKey: 'landing.svcOxidationDesc' },
  { icon: '🔧', titleKey: 'landing.svcDiagTitle', descKey: 'landing.svcDiagDesc' },
];

// Astuces CADBOT : clés i18n de base (traduites au rendu via t()).
const BASE_TIP_KEYS = [
  'landing.tip1', 'landing.tip2', 'landing.tip3', 'landing.tip4', 'landing.tip5',
  'landing.tip6', 'landing.tip7', 'landing.tip8', 'landing.tip9', 'landing.tip10',
];

/** Astuces CADBOT : base publique + jusqu'à 3 prix issus de la page Tarifs (données publiques). */
function buildTips(prices, t, formatCurrency) {
  const fromTarifs = (Array.isArray(prices) ? prices : [])
    .filter((p) => p && p.visible !== false && Number(p.prix_min) > 0 && p.modele && p.type_reparation)
    .slice(0, 3)
    .map((p) => `**${p.type_reparation} ${p.marque || ''} ${p.modele}**`.replace(/\s+/g, ' ')
      + ' ' + t('landing.tipFromPrice', { price: formatCurrency(p.prix_min, 'EUR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }));
  const out = BASE_TIP_KEYS.map((k) => t(k));
  fromTarifs.forEach((tip, i) => out.splice(1 + i * 3, 0, tip));
  return out;
}

const can3d = () => {
  try {
    return typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(hover:hover)').matches
      && window.innerWidth > 640
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, lang, formatCurrency, formatDate } = useLanguage();
  const isFr = lang === 'fr';
  const eur0 = (n) => formatCurrency(n, 'EUR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const [settings, setSettings] = useState(null);
  const [flashOffers, setFlashOffers] = useState([]);
  const [prices, setPrices] = useState([]);
  const [lightbox, setLightbox] = useState(false);
  const [enable3d] = useState(can3d);
  const [caricOpen, setCaricOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/site-settings`).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/api/public/flash-offers`).then((r) => setFlashOffers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/api/public/service-prices`).then((r) => setPrices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const usp = new URLSearchParams(window.location.search);
      if (usp.get('sid') || usp.get('session_id') || window.location.pathname === '/caricature') setCaricOpen(true);
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const s = settings || {};
  const address = s.point_de_vente || DEFAULT_ADDRESS;
  const phone = s.telephone || DEFAULT_PHONE;
  const whatsapp = `https://wa.me/594${(s.whatsapp || phone).replace(/\D/g, '').replace(/^(?:00)?594/, '').replace(/^0/, '')}`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.google_maps_query || `${address} Guyane`)}`;
  const rcs = String(s.rcs || s.rcs_number || '100 156 926').trim(); // la valeur admin peut déjà contenir « RCS Paris … »
  const tips = useMemo(() => buildTips(prices, t, formatCurrency), [prices, t, formatCurrency]);

  return (
    <div className="rmv" data-testid="landing-page">
      <div className="mx max-6xl" style={{ paddingTop: 16 }}><DailyPromoBanner /></div>
      <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
        <button
          type="button"
          onClick={() => window.open('https://www.paypal.com/fr/digital-wallet/ways-to-pay/buy-now-pay-later', '_blank', 'noopener,noreferrer')}
          data-testid="landing-paypal-x4-btn"
          aria-label="Payez en 4 fois sans frais avec PayPal"
          title="Nouveau : payez en plusieurs fois sans frais avec PayPal"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 999,
            background: '#0070ba', color: '#fff', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,.28)', whiteSpace: 'nowrap' }}
        >
          <span aria-hidden="true">💳</span><span>Payez en 4×</span>
        </button>
        <button
          type="button"
          onClick={() => navigate('/aide')}
          data-testid="landing-guide-moi-btn"
          aria-label={t('landing.guideMe')}
          title={t('landing.guideMe')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 999,
            background: '#ef4444', color: '#fff', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,.28)', whiteSpace: 'nowrap' }}
        >
          <LifeBuoy size={16} aria-hidden="true" />
          <span>{t('landing.guideMe')}</span>
        </button>
        <button
          type="button"
          onClick={() => setCaricOpen(true)}
          data-testid="landing-caricature-btn"
          aria-label={t('caric.homeCta')}
          title={t('caric.homeCta')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 999,
            background: '#db2777', color: '#fff', fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,.28)', whiteSpace: 'nowrap' }}
        >
          <Sparkles size={16} aria-hidden="true" />
          <span>{t('caric.homeCta')}</span>
        </button>
        <LanguageSelector />
      </div>

      {/* HERO — nouvelle photo de la boutique en fond */}
      <div className="hero" style={{ backgroundImage: `url(${STORE_PHOTO})` }}>
        <div className="overlay" />
        <div className="mx max-3xl txt">
          <span className="pill"><Smartphone /> {t('landing.mobileRepair')}</span>
          <h1>{s.hero_title || 'Resurrection Mobile'}</h1>
          <p className="sub">{isFr ? (s.hero_subtitle || t('landing.heroSubtitle')) : t('landing.heroSubtitle')}</p>
          <div className="addr"><MapPin /> <span>{address}</span></div>
        </div>
      </div>

      {/* Offres flash (gérées par l'admin) */}
      {flashOffers.length > 0 && (
        <div className="flash" data-testid="flash-offers-section">
          {flashOffers.map((offer, idx) => {
            const end = offer.end_date ? new Date(offer.end_date) : null;
            const endStr = end && !Number.isNaN(end.getTime()) ? formatDate(end, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
            return (
              <div key={offer.id || idx} className="fo" data-testid={`flash-offer-${idx}`}>
                <div className="ib"><Zap /></div>
                <div>
                  <b>-{offer.discount_percent}%</b><strong>{offer.title}</strong>
                  {offer.description && <p>{offer.description}</p>}
                  {endStr && <small>{t('landing.flashUntil', { date: endStr })}</small>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mx max-xl">
        <TrackRepair />

        {/* 3 ESPACES */}
        <div className="spaces" data-testid="login-buttons-section">
          <button type="button" className="sp staff" onClick={() => navigate('/login/staff')} data-testid="btn-espace-staff">
            <div className="ib"><Shield /></div><h3>{t('landing.staffSpace')}</h3><p>{t('landing.staffDesc')}</p>
          </button>
          <button type="button" className="sp client" onClick={() => navigate('/login/client')} data-testid="btn-espace-client">
            <div className="ib"><Smartphone /></div><h3>{t('landing.clientSpace')}</h3><p>{t('landing.clientDesc')}</p>
          </button>
          <button type="button" className="sp biz" onClick={() => navigate('/login/entreprise')} data-testid="btn-espace-entreprise">
            <div className="ib"><MessageCircle /></div><h3>{t('landing.businessSpace')}</h3><p>{t('landing.businessDesc')}</p>
          </button>
        </div>
      </div>

      {/* CADBOT */}
      <div className="sec" id="cadbot">
        <div className="mx max-4xl">
          <h2>{t('landing.cadbotTitle')}</h2>
          <p className="p">{t('landing.cadbotDesc')}</p>
          <CadbotChat address={address} phone={phone} />
        </div>
      </div>

      {/* SERVICES */}
      <div className="sec" data-testid="services-section">
        <div className="mx max-4xl">
          <h2>{t('landing.servicesTitle')}</h2>
          <p className="p">{t('landing.servicesDesc')}</p>
          <div className="svc">
            {SERVICES.map((sv) => (
              <div key={sv.titleKey} className="svc-item"><div className="e">{sv.icon}</div><h3>{t(sv.titleKey)}</h3><p>{t(sv.descKey)}</p></div>
            ))}
          </div>
          <div className="ctas">
            <button type="button" className="btn btn-accent" onClick={() => navigate('/tarifs')} data-testid="cta-tarifs">{t('landing.seeAllPrices')} →</button>
            <button type="button" className="btn btn-sec" onClick={() => navigate('/blog')} data-testid="cta-blog">{t('landing.readBlog')}</button>
          </div>
        </div>
      </div>

      {/* DIAGNOSTIC */}
      <div className="sec" id="diag" style={{ paddingTop: 8 }}>
        <div className="mx max-4xl">
          <h2>{t('landing.diagTitle')}</h2>
          <p className="p">{t('landing.diagDesc')}</p>
          <div className="tiers">
            <div className="tier a"><div className="amt"><i>{t('landing.tierFrom')}</i>{eur0(35)}</div><h3>{t('landing.tierEntryTitle')}</h3><p>{t('landing.tierEntryDesc')}</p></div>
            <div className="tier b"><div className="amt"><i>≈</i>{eur0(55)}</div><h3>{t('landing.tierRecentTitle')}</h3><p>{t('landing.tierRecentDesc')}</p></div>
            <div className="tier c"><div className="amt"><i>{t('landing.tierUpTo')}</i>{eur0(80)}</div><h3>{t('landing.tierComplexTitle')}</h3><p>{t('landing.tierComplexDesc')}</p></div>
          </div>
        </div>
      </div>

      {/* Avis Google (configurés par l'admin) */}
      <div className="mx max-4xl" data-testid="reviews-section"><GoogleReviewsBlock settings={s} isFr={isFr} /></div>

      {/* INFOS : contact + horaires animés | photo boutique + infos légales */}
      <div className="mx max-4xl info">
        <div className="col">
          <div className="card p5">
            <div className="lbl">{t('landing.contact')}</div>
            <a className="crow" href={`tel:${phone}`} data-testid="contact-phone">
              <div className="ib"><Phone /></div><div><b>{phone}</b><small>{t('landing.callWhatsapp')}</small></div>
            </a>
            <a className="crow" href={whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp">
              <div className="ib"><MessageCircle /></div><div><b>WhatsApp</b><small>{t('landing.writeUs')}</small></div>
            </a>
          </div>
          <HoursLive title={t('landing.hours')} />
        </div>
        <div className="col">
          <button type="button" className="shop" onClick={() => setLightbox(true)} title={t('landing.enlarge')} data-testid="store-photo">
            <img src={STORE_PHOTO_SMALL} alt={t('landing.storePhotoAlt')} loading="lazy" />
            <span className="tag">{t('landing.storeTag')}</span>
          </button>
          <div className="card p5 legal" style={{ gap: 8 }}>
            <div className="lbl">{t('landing.legal')}</div>
            <p className="t">{s.company_name || 'Resurrection Mobile'}</p>
            <p>{isFr ? (s.company_type || t('landing.companyType')) : t('landing.companyType')} {t('landing.capital')} {s.capital || '1 000 EUR'}</p>
            <p>{/^\s*rcs/i.test(rcs) ? rcs : `${t('landing.rcs')} ${rcs}`}</p>
            <p>{t('landing.siret')} : {s.siret || '100 156 926 00014'}</p>
            <p>{t('landing.euid')} : {s.euid || 'FR7501.100156926'}</p>
            <p>{t('landing.headquarters')} : {s.siege_social || '60 rue Francois 1er, 75008 Paris'}</p>
            <p className="a"><MapPin /> {t('landing.shopAddress')} : <span>{address}</span></p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
              <a className="btn btn-sec" style={{ padding: '9px 14px', fontSize: 13 }} href={mapsUrl} target="_blank" rel="noreferrer">{t('landing.openMaps')}</a>
            </div>
          </div>
        </div>
      </div>

      {lightbox && (
        <div className="lb" role="dialog" aria-label={t('landing.enlargedPhoto')} onClick={() => setLightbox(false)}>
          <button type="button" aria-label={t('landing.close')} onClick={() => setLightbox(false)}><X /></button>
          <img src={STORE_PHOTO} alt={t('landing.storePhotoAltSimple')} onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <footer>{s.company_name || 'Resurrection Mobile'} - {t('landing.allRights')}</footer>

      {/* Schema.org LocalBusiness */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Resurrection Mobile',
        alternateName: 'R.E.Mobiles',
        url: 'https://remobiles.com',
        image: 'https://remobiles.com/img/magasin-devant.jpg',
        telephone: '+594694274051',
        email: 'contact@remobiles.com',
        address: { '@type': 'PostalAddress', streetAddress: '102 Avenue General de Gaulle, Porte 2', addressLocality: 'Cayenne', postalCode: '97300', addressRegion: 'Guyane', addressCountry: 'FR' },
        geo: { '@type': 'GeoCoordinates', latitude: 4.9333, longitude: -52.3333 },
        priceRange: '$$',
        openingHoursSpecification: [
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '19:00' },
          { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '09:00', closes: '14:00' },
        ],
        sameAs: ['https://www.google.com/maps/place/RESURRECTION+MOBILE'],
        description: 'Reparation de telephones et tablettes a Cayenne, Guyane Francaise. iPhone, Samsung, Huawei. Reparation express, garantie 6 mois, devis gratuit.',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Reparations',
          itemListElement: ['Reparation ecran iPhone', 'Reparation ecran Samsung', 'Remplacement batterie', 'Reparation connecteur de charge', 'Desoxydation telephone']
            .map((n) => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name: n } })),
        },
      }) }} />

      <a className="fab" href="#cadbot" data-testid="homepage-devis-fab"><Bot /> {t('landing.freeQuote')}</a>

      {enable3d && (
        <Suspense fallback={null}><MiniCadbot tips={tips} /></Suspense>
      )}
      <CaricatureModal open={caricOpen} onClose={() => setCaricOpen(false)} />
    </div>
  );
};

export default LandingPage;
